import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ConfidenceBadge } from './ConfidenceBadge';
import { TierBadge } from './TierBadge';
import { CountryBadge } from '../CountryBadge';
import { VendorBadge } from '../vendor-content/VendorBadge';
import { cn } from '../../lib/utils';
import type { StoryCardShape } from '../../types/story';

const SEVERITY_CLASSES: Record<NonNullable<StoryCardShape['maxSeverity']>, string> = {
  critical: 'text-rose-700 dark:text-rose-300',
  high:     'text-amber-700 dark:text-amber-300',
  medium:   'text-sky-700 dark:text-sky-300',
  low:      'text-neutral-600 dark:text-neutral-400',
};

export function StoryCard({ story }: { story: StoryCardShape }) {
  const updated = formatDistanceToNow(new Date(story.latestSourceAddedAt), { addSuffix: true });

  return (
    <article
      className={cn(
        'group rounded-lg border bg-card p-4 transition-shadow hover:shadow-md',
        story.isUnverified && 'border-dashed border-rose-500/30',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <Link
          to={`/stories/${story.id}`}
          className="line-clamp-2 text-base font-semibold leading-snug text-foreground transition-colors hover:text-primary"
        >
          {story.title}
        </Link>
        <ConfidenceBadge label={story.confidenceLabel} />
      </div>

      {/* KAN-822 T9 — Vendor stories are labelled (dark build flag). */}
      <div className="mt-2 empty:hidden">
        <VendorBadge story={story} />
      </div>

      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{story.excerpt}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1 font-medium text-foreground">
          {story.publisherBadge.name}
          <TierBadge tier={story.publisherBadge.tier} />
          <CountryBadge country={story.originCountry ?? null} />
        </span>
        <span>·</span>
        <span>{story.sourceCount} sources</span>
        <span>·</span>
        <span>{story.independentOrgs} orgs</span>
        {story.maxSeverity && (
          <>
            <span>·</span>
            <span className={cn('uppercase font-semibold', SEVERITY_CLASSES[story.maxSeverity])}>
              {story.maxSeverity}
            </span>
          </>
        )}
        <span className="ml-auto">{updated}</span>
      </div>
    </article>
  );
}
